// Lyric lines with their inline "[G]" chords, as the live page and the previews show them.
import { transposeChord } from "./chords";

const CHORD = /\[([^\]]*)\]/g;
const ANY_CHORD = /\[[^\]]*\]/;

export const hasChords = (text) => ANY_CHORD.test(text || "");

export const songHasChords = (song) => (song?.Verses || []).some((verse) => hasChords(verse.Lines));

/** The text without its chord markers, spaces tidied. */
export const stripChords = (text) => (text || "").replace(CHORD, "").replace(/[ \t]+/g, " ").trim();

/** A verse's text as separate lines (no trailing empties). */
export const splitLines = (text) =>
  (text || "")
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((line) => line.replace(/\s+$/, ""))
    .filter((line, index, lines) => line.length > 0 || index < lines.length - 1);

/**
 * One line split into segments, each with the chord that starts it (transposed) and the
 * words under it: "Amazing [G]grace" -> [{chord: null, text: "Amazing "}, {chord: "G", text: "grace"}].
 */
export function parseLine(line, semitones = 0) {
  const segments = [];
  let last = 0;
  let chord = null;
  for (const match of (line || "").matchAll(CHORD)) {
    const text = line.slice(last, match.index);
    if (text || chord !== null) segments.push({ chord, text });
    chord = transposeChord(match[1], semitones);
    last = match.index + match[0].length;
  }
  const rest = line.slice(last);
  if (rest || chord !== null) segments.push({ chord, text: rest });
  if (segments.length === 0) segments.push({ chord: null, text: "" });
  return segments;
}

/** The chords of a line as one spaced string ("G  C  G"), for compact previews. */
export const lineChords = (line, semitones = 0) =>
  (line || "")
    .match(CHORD)
    ?.map((marker) => transposeChord(marker.slice(1, -1), semitones))
    .join("  ") || "";

/** Every line of a verse, as plain text and as segments. */
export const verseLines = (verse, semitones = 0) =>
  splitLines(verse?.Lines).map((line) => ({
    text: stripChords(line),
    hasChords: hasChords(line),
    segments: parseLine(line, semitones),
  }));

/** The first few lines of a verse without chords (tiles and previews). */
export const firstLines = (verse, count = 2) =>
  splitLines(verse?.Lines)
    .map(stripChords)
    .filter(Boolean)
    .slice(0, count);

/** The last few lines of a verse without chords (the fading tail above the live section). */
export const lastLines = (verse, count = 2) =>
  splitLines(verse?.Lines)
    .map(stripChords)
    .filter(Boolean)
    .slice(-count);

/** A verse's label, numbered when the song left it empty ("Verse 2"). */
export const verseLabel = (verse, index) => (verse?.Header || "").trim() || `Section ${(index ?? verse?.Order ?? 0) + 1}`;

/** The verses in display order. */
export const sortedVerses = (song) => [...(song?.Verses || [])].sort((a, b) => a.Order - b.Order);
